import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import {
  productSelector,
  selectedProductTariffHour,
  tariffsState,
  updateProductAvailableDateTariff,
  type FullProduct,
  type TariffsPerType,
} from '@src/stores/product';
import type { ScheduleTime } from '@src/core/buyerApiClient';
import type { HourListItem } from '@src/domains/products/features/productAdder/types/productAdder';

import { getSelectedTariffsPerType } from '@src/domains/tariffs';
import {
  getDisplayableTariffs,
  sortTariffsByAvailability,
  type TariffsPerTypeExtended,
} from '@src/domains/tariffs/core/tariffDisplay';
import {
  acceptableQuantities,
  calculateSelectedTariffIndex,
  getTariffDescription,
  isTariffDependent as isTariffDependentRule,
} from '@src/domains/tariffs/core/tariffSelection';
import { computeTariffPriceInfo } from '@src/domains/tariffs/core/tariffPrice';
import {
  shouldShowIndependentTariffNotSelectedError,
  shouldShowMaxAvailabilityReachedError,
  shouldShowMultipleQuantityError,
  shouldShowSingleQuantityError,
} from '@src/domains/tariffs/core/tariffErrorDisplay';
import { isTotalQuantityExceedingWholeDateLimit } from '@src/domains/tariffs/core/tariffQuantityLimit';

type UseTariffSelectionParams = {
  /**
   * Hours visible for a with_date_and_time product. v1 passes the time-filtered list so the
   * tariff list is narrowed to the selected hour; v2 (separate TimeSelector) passes none.
   */
  hoursList?: readonly HourListItem[];
};

export type UseTariffSelectionReturn = {
  product: FullProduct | undefined;
  listOfTariffsPerType: TariffsPerType[];
  selectedHour: ScheduleTime | undefined;
  tariffs: TariffsPerTypeExtended[];
  onUpdateQuantity: (tariffTypeId: string, quantity: number) => void;
  acceptableQuantitiesFor: (tariff: TariffsPerType) => [number, number];
  priceInfoFor: (tariff: TariffsPerType, useOldPrice?: boolean) => string;
  descriptionFor: (tariff: TariffsPerType) => string;
  isDependent: (tariffTypeId: string) => boolean;
  calculateTariffIndex: (index: number) => number | null;
  showSingleError: (tariffTypeId: string) => boolean;
  showMultipleError: boolean;
  showIndependentError: (tariffTypeId: string) => boolean;
  showMaxAvailabilityError: (tariff: TariffsPerType) => boolean;
};

const EMPTY_HOURS: readonly HourListItem[] = [];

/**
 * Tariff-selection orchestration shared by the v1 ProductAdderTariff component and the v2
 * useTariffSelector hook: the displayable/sorted tariff list plus the per-tariff presenters
 * (price, description, dependency) and the quantity/error rules. Both wired the exact same
 * selectors (selectedHour, getSelectedTariffsPerType) on top of the tariffs/core rules — this
 * removes that duplication. Consumers keep their own UI shell and feature-specific state.
 */
export function useTariffSelection({
  hoursList = EMPTY_HOURS,
}: UseTariffSelectionParams = {}): UseTariffSelectionReturn {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const productId = useSelectedProductId();
  const product = useAppSelector(productSelector(productId));
  const tariffsInformation = useAppSelector(tariffsState(widgetId));
  const { listOfTariffsPerType } = tariffsInformation;
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));

  const tariffs = useMemo<TariffsPerTypeExtended[]>(() => {
    if (!product) return [];
    const selected = getSelectedTariffsPerType(listOfTariffsPerType);
    const displayable = getDisplayableTariffs(listOfTariffsPerType, selected, hoursList);
    return sortTariffsByAvailability(displayable, product);
  }, [listOfTariffsPerType, product, hoursList]);

  const onUpdateQuantity = useCallback(
    (tariffTypeId: string, quantity: number) => {
      const tariffPerType = listOfTariffsPerType.find((t) => t.type.id === tariffTypeId);
      if (!tariffPerType) return;
      dispatch(
        updateProductAvailableDateTariff({ id: widgetId, value: { ...tariffPerType, selectedQuantity: quantity } }),
      );
    },
    [dispatch, widgetId, listOfTariffsPerType],
  );

  const acceptableQuantitiesFor = useCallback(
    (tariff: TariffsPerType): [number, number] => {
      if (!product) return [0, 0];
      return acceptableQuantities(tariff, tariffsInformation, product, selectedHour);
    },
    [product, tariffsInformation, selectedHour],
  );

  const priceInfoFor = useCallback(
    (tariff: TariffsPerType, useOldPrice = false): string =>
      computeTariffPriceInfo(tariff, selectedHour, useOldPrice, i18n('adder_price_to')),
    [i18n, selectedHour],
  );

  const descriptionFor = useCallback(
    (tariff: TariffsPerType): string => getTariffDescription(tariff, selectedHour),
    [selectedHour],
  );

  const isDependent = useCallback(
    (tariffTypeId: string): boolean => isTariffDependentRule(tariffTypeId, listOfTariffsPerType, selectedHour),
    [listOfTariffsPerType, selectedHour],
  );

  const calculateTariffIndex = useCallback(
    (index: number): number | null => calculateSelectedTariffIndex(tariffs, index),
    [tariffs],
  );

  const isTotalQtyExceedingLimit = useMemo(
    () => isTotalQuantityExceedingWholeDateLimit(listOfTariffsPerType),
    [listOfTariffsPerType],
  );

  const showSingleError = useCallback(
    (tariffTypeId: string): boolean =>
      !!product && shouldShowSingleQuantityError(tariffTypeId, product, listOfTariffsPerType, isTotalQtyExceedingLimit),
    [product, listOfTariffsPerType, isTotalQtyExceedingLimit],
  );

  const showMultipleError = useMemo(
    () => (product ? shouldShowMultipleQuantityError(product, isTotalQtyExceedingLimit) : false),
    [product, isTotalQtyExceedingLimit],
  );

  const showIndependentError = useCallback(
    (tariffTypeId: string): boolean =>
      shouldShowIndependentTariffNotSelectedError(tariffTypeId, listOfTariffsPerType, selectedHour),
    [listOfTariffsPerType, selectedHour],
  );

  const showMaxAvailabilityError = useCallback(
    (tariff: TariffsPerType): boolean =>
      !!product && shouldShowMaxAvailabilityReachedError(tariff, product, tariffsInformation, selectedHour),
    [product, tariffsInformation, selectedHour],
  );

  return {
    product,
    listOfTariffsPerType,
    selectedHour,
    tariffs,
    onUpdateQuantity,
    acceptableQuantitiesFor,
    priceInfoFor,
    descriptionFor,
    isDependent,
    calculateTariffIndex,
    showSingleError,
    showMultipleError,
    showIndependentError,
    showMaxAvailabilityError,
  };
}
